import type { ApolloClient } from '@apollo/client';
import type { AssemblyResponse } from '@uppy/transloadit';
import URI from 'jsuri';
import Q from 'q';

import API from 'js/lib/api';
import { get } from 'js/lib/user';

import UppyAssetUploadSources from 'bundles/asset-admin/constants/UppyAssetUploadSources';
import AuthoringAssetCreationAttemptsMutation from 'bundles/asset-admin/data/AuthoringAssetCreationAttemptsMutation.graphql';
import type {
  AuthoringAssetCreationAttemptsMutationMutation,
  AuthoringAssetCreationAttemptsMutationMutationVariables,
} from 'bundles/asset-admin/data/__generated__/AuthoringAssetCreationAttemptsMutation';
import type { AssetContext, AssetTag, PendingAssetCreationData } from 'bundles/asset-admin/types/assets';
import type {
  AssetAssembly,
  AssetOptions,
  FileAttempt,
  TransloaditAssetFile,
  UppyFile,
  ValidAssetSources,
} from 'bundles/asset-admin/types/uppy';
import {
  getAssetCreationInput,
  getAssetType,
  getTypeSpecificOptions,
} from 'bundles/asset-admin/utils/AssetUploaderUtils';
import { isGraphqlCreateAuthoringAssetCreationEnabled } from 'bundles/authoring/featureFlags';

const assetCreationAttemptsAPI = API('/api/assetCreationAttempts.v1/', { type: 'rest' });
const authoringAssetCreationUrl = '/api/authoringAssetCreationAttempts.v1';

export const createAuthoringAssetCreationAttempt = async ({
  pendingAssetCreationData,
  context,
  apolloClient,
  file,
}: {
  pendingAssetCreationData: PendingAssetCreationData;
  context: AssetContext;
  apolloClient: ApolloClient<unknown>;
  file: UppyFile;
}): Promise<FileAttempt & { attemptId: string }> => {
  const creationInput = getAssetCreationInput({
    pendingAssetCreationData,
    file,
    context,
  });

  const { data, errors } = await apolloClient.mutate<
    AuthoringAssetCreationAttemptsMutationMutation,
    AuthoringAssetCreationAttemptsMutationMutationVariables
  >({
    mutation: AuthoringAssetCreationAttemptsMutation,
    variables: {
      input: {
        ...creationInput,
        assetTags: creationInput.assetTags.length
          ? creationInput.assetTags
          : [
              {
                name: 'scope',
                value: pendingAssetCreationData?.assetType,
              },
            ],
      },
    },
    context: { clientName: 'gatewayGql' },
  });

  if (errors) {
    throw errors;
  }

  const attemptId = data?.AuthoringAssetCreationAttempts_Create?.id;

  if (!attemptId) {
    throw new Error('Asset creation attempt failed');
  }

  return { ...file, attemptId };
};

export const createAssetCreationAttempt = (
  assetOptions: AssetOptions,
  context: AssetContext,
  file: UppyFile,
  assetCreationUrl = authoringAssetCreationUrl,
  additionalAssetTags: Array<AssetTag> = [],
  apolloClient?: ApolloClient<unknown>
): Q.Promise<FileAttempt & { attemptId: string }> => {
  const typeName = 'pending';
  const assetType = getAssetType(file);
  const typeSpecificOptions = getTypeSpecificOptions(assetType);

  const pendingAssetCreation = {
    assetType,
    typeName,
    ...typeSpecificOptions,
    tags: [...typeSpecificOptions.tags, ...additionalAssetTags],
    ...assetOptions,
    ...(assetCreationUrl === authoringAssetCreationUrl ? { creatorId: get()?.id } : {}),
  };

  const data = {
    pendingAssetCreation,
    context,
  };

  if (
    assetCreationUrl === authoringAssetCreationUrl &&
    apolloClient &&
    isGraphqlCreateAuthoringAssetCreationEnabled()
  ) {
    return Q(
      createAuthoringAssetCreationAttempt({
        pendingAssetCreationData: pendingAssetCreation,
        context,
        apolloClient,
        file,
      })
    );
  }

  // default asset creation endpoint is the authoring one, but for new use cases like
  // learner-side forums we override this to use product-specific urls to avoid permission issues
  const assetCreationAttemptAPI = API(assetCreationUrl, { type: 'rest' });

  return Q(assetCreationAttemptAPI.post('', { data })).then((resp) => {
    const attemptId = resp.elements[0].id;
    return { ...file, attemptId };
  });
};

// this looks like it handles multiple assets but it doesnt actually seem to, since attemptIds are unique per asset.
export const succeedAssetCreationAttempt = (
  attemptId: string,
  asset: TransloaditAssetFile,
  uppyFileSource: string,
  assembly: AssetAssembly
): Q.Promise<void> => {
  const uri = new URI(attemptId);

  const assets = [
    {
      filename: asset.name,
      mime: asset.mime,
      size: asset.size,
      sslUrl: asset.ssl_url,
      url: asset.url,
      pageCount: asset.meta.page_count,
      duration: asset.meta.duration,
      audioBitRate: asset.meta.audio_bitrate,
      subtitleType: asset.meta.subtitle_type,
      sourceType: UppyAssetUploadSources[uppyFileSource as keyof typeof UppyAssetUploadSources] as ValidAssetSources,
    },
  ];

  const data = {
    typeName: 'successful',
    transloaditData: JSON.stringify(assembly),
    assets,
  };

  return Q(assetCreationAttemptsAPI.put(uri.toString(), { data }));
};

export const failAssetCreationAttempt = (assembly: AssemblyResponse): Q.Promise<Array<string>> => {
  const uri = new URI(assembly?.fields?.attemptId);

  const data = {
    typeName: 'failed',
    transloaditData: JSON.stringify(assembly),
    errorMessage: assembly.error,
  };

  return Q(assetCreationAttemptsAPI.put(uri.toString(), { data })).then((resp) =>
    resp.elements.map((element: { id: number }) => element.id)
  );
};
